/**
 * 3PH Cloud study version labels — from Study.VersionID on the live ODM document.
 * Format: major.minor.patch.build (patch = YYDDD, build = same-day revision count).
 */

export interface OdmStudyVersionMeta {
  studyName: string
  studyOid: string
  versionId: string
  fileOid: string | null
}

const VERSION_ID_RE = /^\d+\.\d+\.\d{5}\.\d+$/

/**
 * Day-of-year patch segment (YYDDD) per Study-Build-Process.docx.
 *
 * UTC, not local. The same segment is computed server-side by
 * shell_renderer._version_id, which normalises to UTC, and the value ends up
 * in Study/@VersionID on a document that travels to Anvil and is compared
 * across timezones. A local-day reading disagrees with the ODM it is meant to
 * describe for every user west of UTC between 00:00Z and local midnight —
 * which is how the sidebar showed v1.0.26244.1 next to a live ODM of
 * v1.0.26245.1. It also silently disabled the same-day build bump in
 * suggestOdmArtifactBasename, since that compares this value against a patch
 * segment parsed out of the XML.
 *
 * Both endpoints are truncated to UTC midnight, so the subtraction is an
 * exact whole number of days with no DST term.
 */
export function formatOdmPatchSegment(date = new Date()): string {
  const year = date.getUTCFullYear()
  const jan1 = Date.UTC(year, 0, 1)
  const today = Date.UTC(year, date.getUTCMonth(), date.getUTCDate())
  const dayOfYear = Math.round((today - jan1) / 86_400_000) + 1
  return `${String(year % 100).padStart(2, '0')}${String(dayOfYear).padStart(3, '0')}`
}

export function isOdmVersionId(value: string): boolean {
  return VERSION_ID_RE.test(value.trim())
}

export function parseOdmStudyVersionMeta(xml: string): OdmStudyVersionMeta | null {
  if (!xml?.trim()) return null
  try {
    const doc = new DOMParser().parseFromString(xml, 'text/xml')
    if (doc.querySelector('parsererror')) return null

    const study = doc.querySelector('Study')
    if (!study) return null

    const versionId = (study.getAttribute('VersionID') || '').trim()
    if (!versionId) return null

    const studyName = (study.getAttribute('StudyName') || '').trim()
    const studyOid = (study.getAttribute('OID') || '').trim()
    const fileOid = doc.documentElement?.getAttribute('FileOID')?.trim() || null

    return {
      studyName: studyName || studyOid.replace(/\.1$/, ''),
      studyOid,
      versionId,
      fileOid,
    }
  } catch {
    return null
  }
}

/** Reads only the identity fields, so callers can pass a partial meta. */
export function studyCodeFromOdm(
  meta: Pick<OdmStudyVersionMeta, 'studyName' | 'studyOid'>,
): string {
  if (meta.studyName) return meta.studyName
  return meta.studyOid.replace(/\.1$/, '') || 'STUDY'
}

/** Filesystem-safe basename: STUDY-ABC_1.0.26182.1 */
export function formatOdmArtifactBasename(meta: OdmStudyVersionMeta): string {
  const code = studyCodeFromOdm(meta)
    .replace(/[^\w-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `${code || 'study'}_${meta.versionId}`
}

export function formatOdmArtifactFilename(meta: OdmStudyVersionMeta): string {
  return `${formatOdmArtifactBasename(meta)}.xml`
}

/** Bump the build segment (last component) for a same-day save. */
export function incrementOdmBuildVersion(versionId: string): string {
  const parts = versionId.split('.')
  if (parts.length < 4) return versionId
  const build = parseInt(parts[parts.length - 1]!, 10)
  if (Number.isNaN(build)) return versionId
  parts[parts.length - 1] = String(build + 1)
  return parts.join('.')
}

export interface SaveBasenameOptions {
  /** File versions already in the session drawer (for same-day build bump). */
  existingFilenames?: string[]
  /** Active workspace study code — used when XML still carries a demo or stale StudyName. */
  studyCode?: string
}

function normalizeStudyCode(value: string): string {
  return value.trim().toUpperCase().replace(/[^\w-]+/g, '-').replace(/-+/g, '-')
}

/**
 * Prefer workspace study code when XML still names the generic demo scaffold or
 * disagrees with the study the user opened in Study Design.
 */
export function resolveArtifactStudyCode(
  meta: OdmStudyVersionMeta,
  workspaceStudyCode?: string,
): string {
  const xmlCode = studyCodeFromOdm(meta)
  const override = workspaceStudyCode?.trim()
  if (!override) return xmlCode

  const xmlNorm = normalizeStudyCode(xmlCode)
  const workspaceNorm = normalizeStudyCode(override)
  if (xmlNorm === workspaceNorm) return xmlCode

  // Leftover incremental-demo XML while building a different study.
  if (xmlNorm === 'STUDY-ABC' && workspaceNorm !== 'STUDY-ABC') {
    return override
  }

  if (xmlNorm !== workspaceNorm) return override
  return xmlCode
}

/**
 * Default save/snapshot basename from merged ODM XML.
 * Bumps build if a file with the same major.minor.patch already exists today.
 */
export function suggestOdmArtifactBasename(
  xml: string,
  options: SaveBasenameOptions = {},
): string {
  const meta = parseOdmStudyVersionMeta(xml)
  if (!meta) {
    if (options.studyCode?.trim()) {
      const code = options.studyCode.trim()
        .replace(/[^\w-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '')
      return `${code || 'study'}_odm`
    }
    return 'study_odm'
  }

  const code = resolveArtifactStudyCode(meta, options.studyCode)

  let versionId = meta.versionId
  const patch = versionId.split('.')[2]
  const todayPatch = formatOdmPatchSegment()

  const codePrefix = code
    .replace(/[^\w-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()

  const siblings = (options.existingFilenames ?? []).filter(name => {
    const lower = name.toLowerCase()
    return lower.startsWith(codePrefix) && lower.includes(`_${versionId.split('.').slice(0, 3).join('.')}`)
  })

  if (patch === todayPatch && siblings.some(f => f.includes(versionId))) {
    versionId = incrementOdmBuildVersion(versionId)
  }

  return formatOdmArtifactBasename({ ...meta, studyName: code, versionId })
}

/** Parse 3PH VersionID embedded in an artifact basename or filename. */
export function extractOdmVersionFromFilename(filename: string): string | null {
  const base = filename.replace(/\.[^.]+$/, '')
  const match = base.match(/_(\d+\.\d+\.\d{5}\.\d+)$/)
  return match?.[1] ?? null
}

export interface FileVersionRef {
  filename: string
  version: string
}

/** Numeric segment compare — works for both drawer (1.2) and ODM (1.0.26182.3) versions. */
export function compareVersionIds(a: string, b: string): number {
  const pa = a.split('.').map(part => parseInt(part, 10))
  const pb = b.split('.').map(part => parseInt(part, 10))
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const av = pa[i]
    const bv = pb[i]
    if (Number.isNaN(av) || Number.isNaN(bv)) return a.localeCompare(b)
    const diff = av - bv
    if (diff !== 0) return diff
  }
  return 0
}

export function latestVersionForFilename(
  filename: string,
  versions: FileVersionRef[],
): FileVersionRef | null {
  const matches = versions.filter(v => v.filename === filename)
  if (matches.length === 0) return null
  return matches.reduce((best, cur) =>
    compareVersionIds(cur.version, best.version) > 0 ? cur : best,
  )
}

/** Display label for drawer badges and comparator headers. */
export function formatFileVersionLabel(version: string, filename: string): string {
  const fromName = extractOdmVersionFromFilename(filename)
  const id = isOdmVersionId(version) ? version : (fromName ?? version)
  return `v${id}`
}

/** Dropdown row — avoid duplicating v1.0.26182.1 when the filename already encodes it. */
export function fileVersionOptionLabel(version: string, filename: string): string {
  const label = formatFileVersionLabel(version, filename)
  const fromName = extractOdmVersionFromFilename(filename)
  if (fromName && (version === fromName || label === `v${fromName}`)) {
    return filename
  }
  return `${label} — ${filename}`
}

/**
 * Resolve parent + explicit version for POST /api/files/versions.
 * Uses the versions index (not uploadedFiles) and prefers ODM Study@VersionID.
 */
export function resolveSaveVersionParams(
  xml: string,
  filename: string,
  allVersions: FileVersionRef[],
): { parentVersion: string; version?: string } {
  const meta = parseOdmStudyVersionMeta(xml)
  const fromFilename = extractOdmVersionFromFilename(filename)
  const odmFromXml = meta?.versionId

  const latest = latestVersionForFilename(filename, allVersions)

  if (latest) {
    const base = isOdmVersionId(latest.version)
      ? latest.version
      : (odmFromXml && isOdmVersionId(odmFromXml) ? odmFromXml : latest.version)
    if (isOdmVersionId(base)) {
      return { parentVersion: base, version: incrementOdmBuildVersion(base) }
    }
    return { parentVersion: latest.version }
  }

  const target = fromFilename ?? odmFromXml
  if (target && isOdmVersionId(target)) {
    return { parentVersion: target, version: target }
  }

  return { parentVersion: '0.0' }
}
